<template>
    <div class="skill-list-table" v-if="items.length > 0">
        <div class="skill-list-table__toolbar" v-if="mode === 'view'">
            <v-menu
                v-model="filterOpen"
                :close-on-content-click="false"
                offset-y
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        v-bind="attrs"
                        v-on="on"
                    >
                        <v-icon size="26" :color="activeFilterCount ? 'primary' : undefined">mdi-filter-variant</v-icon>
                    </v-btn>
                </template>
                <v-card class="skill-list-table__filter-popup">
                    <v-card-text>
                        <v-text-field
                            v-model="search"
                            :label="$t('pokedex.skillListTable.searchLabel')"
                            append-icon="mdi-magnify"
                            autofocus
                            clearable
                            hide-details
                            dense
                            class="mb-3"
                        />

                        <v-select
                            v-model="typeFilter"
                            :items="availableTypes"
                            item-text="name"
                            item-value="id"
                            :label="$t('pokedex.common.typeLabel')"
                            multiple
                            chips
                            small-chips
                            deletable-chips
                            clearable
                            hide-details
                            dense
                            class="mb-3"
                        />

                        <v-select
                            v-model="useFilter"
                            :items="useFilterOptions"
                            :label="$t('pokedex.common.useLabel')"
                            multiple
                            chips
                            small-chips
                            deletable-chips
                            clearable
                            hide-details
                            dense
                            class="mb-3"
                        />

                        <v-select
                            v-model="areaFilter"
                            :items="areaFilterOptions"
                            :label="$t('pokedex.common.areaEffectLabel')"
                            hide-details
                            dense
                            class="mb-3"
                        />

                        <v-select
                            v-model="effectFilter"
                            :items="availableEffects"
                            :label="$t('pokedex.skillListTable.specialEffectLabel')"
                            multiple
                            chips
                            small-chips
                            deletable-chips
                            clearable
                            hide-details
                            dense
                        />
                    </v-card-text>
                </v-card>
            </v-menu>
            <span v-if="activeFilterCount" class="skill-list-table__filter-hint">{{ $tc('pokedex.skillListTable.filtersActiveCount', activeFilterCount, {n: activeFilterCount}) }}</span>

            <v-spacer/>

            <v-btn
                icon
                :title="expanded ? $t('pokedex.skillListTable.collapseTable') : $t('pokedex.skillListTable.expandTable')"
                @click="expanded = !expanded"
            >
                <v-icon size="26" :color="expanded ? 'primary' : undefined">
                    {{ expanded ? 'mdi-arrow-collapse-horizontal' : 'mdi-arrow-expand-horizontal' }}
                </v-icon>
            </v-btn>
        </div>
        <div class="skill-list-table__toolbar" v-else>
            <v-spacer/>
            <v-btn
                icon
                :title="expanded ? $t('pokedex.skillListTable.collapseTable') : $t('pokedex.skillListTable.expandTable')"
                @click="expanded = !expanded"
            >
                <v-icon size="26" :color="expanded ? 'primary' : undefined">
                    {{ expanded ? 'mdi-arrow-collapse-horizontal' : 'mdi-arrow-expand-horizontal' }}
                </v-icon>
            </v-btn>
        </div>

        <div class="skill-list-table__rows" :class="{ 'skill-list-table__rows--expanded': expanded, 'skill-list-table__rows--buy': mode === 'buy', 'skill-list-table__rows--buy-left': mode === 'buy' && rightHanded }">
            <div class="skill-row skill-list-table__header-row">
                <span class="skill-row__name">{{ $t('pokedex.skillListTable.nameColumn') }}</span>
                <span class="skill-row__type">{{ $t('pokedex.common.typeLabel') }}</span>
                <span class="skill-row__use" :title="$t('pokedex.common.useLabel')">{{ $t('pokedex.skillListTable.useColumnHeader') }}</span>
                <span class="skill-row__aoe" :title="$t('pokedex.common.areaEffectLabel')">{{ $t('pokedex.skillListTable.areaColumnHeader') }}</span>
                <span class="skill-row__dmg">{{ $t('pokedex.skillListTable.dmgColumn') }}</span>
                <span class="skill-row__formula">{{ $t('pokedex.skillListTable.atkColumn') }}</span>
                <span class="skill-row__bonus">{{ $t('pokedex.skillListTable.bonusColumn') }}</span>
                <span class="skill-row__effect">{{ $t('pokedex.common.effectLabel') }}</span>
                <span class="skill-row__cost">{{ mode === 'buy' ? $t('pokedex.skillListTable.expColumnHeader') : $t('pokedex.skillListTable.costColumnHeader') }}</span>
                <span class="skill-row__level">{{ $t('pokedex.skillListTable.levelColumn') }}</span>
                <span v-if="mode === 'buy'" class="skill-row__buy">{{ $t('pokedex.skillListTable.buyColumn') }}</span>
            </div>
            <div
                v-for="item in displayedItems"
                :key="item.id"
                class="skill-row"
                :class="[textColors[item.type.id] ? 'white--text' : 'black--text', {'skill-row--clickable': hasOpenDetailListener, 'skill-row--disabled': isSkillDisabled(item)}]"
                :style="'background-color: ' + rowBg(item)"
                @click="$emit('open-detail', item)"
            >
                <div class="skill-row__name">
                    <v-icon size="16" :color="textColors[item.type.id] ? 'white' : 'black'">{{ rowIcon(item) }}</v-icon>
                    <span>{{ item.name }}</span>
                </div>

                <TypePill :type="item.type" size="x-small" class="skill-row__type">{{ $t('common.typeNames.' + item.type.name) }}</TypePill>

                <v-icon class="skill-row__use" size="16" :title="useLabelText(item)" :color="textColors[item.type.id] ? 'white' : 'black'">{{ useIcon(item) }}</v-icon>

                <v-icon v-if="item.spread" class="skill-row__aoe" size="16" :title="$t('pokedex.common.areaEffectLabel')" :color="textColors[item.type.id] ? 'white' : 'black'">mdi-earth</v-icon>
                <span v-else class="skill-row__aoe"/>

                <span class="skill-row__dmg">{{ item.minDmg }}-{{ item.maxDmg }}</span>

                <span class="skill-row__formula">{{ item.attackFormula }}</span>
                <span class="skill-row__bonus">{{ item.dmgFormula }}</span>
                <span class="skill-row__effect">{{ getEffectFormula(item) }}</span>
                <span class="skill-row__cost">{{ item.cost }}</span>
                <span class="skill-row__level">{{ item.lvl }}</span>

                <div v-if="mode === 'buy'" class="skill-row__buy">
                    <v-icon v-if="haveSkill(item)" color="success">mdi-check-bold</v-icon>
                    <v-btn
                        v-else
                        x-small
                        rounded
                        class="skin-btn-buy"
                        :loading="loading"
                        :disabled="!canBuy(item)"
                        @click.stop="$emit('buy', item.id)"
                    >
                        {{ $t('pokedex.skillListTable.buyButton') }}
                    </v-btn>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    // Sjednocena skill-tabulka - nahrazuje 3 nezavisle implementace stejne tabulky (SkillsTable.vue,
    // SkillsBuyTable.vue, bespoke v-data-table v PokemonDetail.vue). `mode: 'view'` = read-only
    // (Pokedex/globalni /skills), `mode: 'buy'` = nakup skillu pro pokemona (Buy sloupec,
    // haveSkill/canBuy logika).
    import {TYPE_COLORS, TYPE_GREY_COLORS, TYPE_DARK_THEME} from "@/utils/typeColorEnum";
    import {useIcon, useLabel, getEffectFormula, getRgba} from "@/utils/skillDisplay";
    import {flags} from '@/config/featureFlags';
    import TypePill from "@/components/utils/TypePill";

    export default {
        name: 'SkillListTable',
        components: {TypePill},
        props: {
            items: {type: Array, default: () => []},
            mode: {
                type: String,
                default: 'view',
                validator: (v) => ['view', 'buy'].includes(v),
            },
            pokemon: {type: Object, default: null},
            loading: {type: Boolean, default: false},
            // ID skillu, ktere se maji vykreslit sedive (zadani uzivatele
            // 2026-08-27 - dungeon "uz dnes pouzity" pool, viz
            // PokemonPickerCard.vue). Relevantni jen v mode="view" - "buy"
            // mod uz ma vlastni sedivou logiku (haveSkill()).
            disabledSkillIds: {type: Array, default: () => []},
        },
        data() {
            return {
                search: '',
                filterOpen: false,
                expanded: false,
                typeFilter: [],
                useFilter: [],
                areaFilter: null,
                effectFilter: [],
                colors: TYPE_COLORS,
                colorsGrey: TYPE_GREY_COLORS,
                textColors: TYPE_DARK_THEME,
            }
        },
        created() {
            // Vue 2 prop validator nema pristup k sourozeneckym props, nejde zkontrolovat primo
            // tam - runtime kontrola aspon nahlas rekne, proc haveSkill/canBuy/isFirst spadnou na null.
            if (this.mode === 'buy' && !this.pokemon) {
                console.error('SkillListTable: mode="buy" vyzaduje pokemon prop.');
            }
        },
        computed: {
            // Radek vypada klikaci jen kdyz volajici realne poslouchá open-detail - jinak by
            // kurzor slibovala klikatelnost, ktera nic nedela.
            hasOpenDetailListener() {
                return !!this.$listeners['open-detail'];
            },
            rightHanded() {
                return flags.rightHanded;
            },
            // v-select :items - hodnoty ('Attack'/'Dodge'/'Shield') musi zustat anglicky
            // beze zmeny (porovnavaji se s useLabel(item) v displayedItems filtru nize),
            // lokalizuje se jen zobrazeny text.
            useFilterOptions() {
                return [
                    {text: this.$t('pokedex.common.useAttack'), value: 'Attack'},
                    {text: this.$t('pokedex.common.useDodge'), value: 'Dodge'},
                    {text: this.$t('pokedex.common.useShield'), value: 'Shield'},
                ];
            },
            areaFilterOptions() {
                return [
                    {text: this.$t('pokedex.skillListTable.areaFilterAny'), value: null},
                    {text: this.$t('pokedex.skillListTable.areaFilterAreaOnly'), value: true},
                    {text: this.$t('pokedex.skillListTable.areaFilterSingleOnly'), value: false},
                ];
            },
            availableTypes() {
                const byId = new Map();
                this.items.forEach((item) => byId.set(item.type.id, item.type));
                return Array.from(byId.values());
            },
            availableEffects() {
                const effects = new Set();
                this.items.forEach((item) => {
                    if (item.effect) effects.add(item.effect);
                });
                return Array.from(effects).sort();
            },
            activeFilterCount() {
                return (this.search ? 1 : 0)
                    + (this.typeFilter.length ? 1 : 0)
                    + (this.useFilter.length ? 1 : 0)
                    + (this.areaFilter !== null ? 1 : 0)
                    + (this.effectFilter.length ? 1 : 0);
            },
            displayedItems() {
                if (this.mode === 'buy') {
                    return this.items;
                }
                const q = this.search ? this.search.toLowerCase() : '';
                return this.items.filter((item) => {
                    if (q) {
                        const nameMatch = item.name.toLowerCase().includes(q);
                        const effectMatch = item.effect && item.effect.toLowerCase().includes(q);
                        if (!nameMatch && !effectMatch) return false;
                    }
                    if (this.typeFilter.length && !this.typeFilter.includes(item.type.id)) return false;
                    if (this.useFilter.length && !this.useFilter.includes(useLabel(item))) return false;
                    if (this.areaFilter !== null && !!item.spread !== this.areaFilter) return false;
                    if (this.effectFilter.length && !this.effectFilter.includes(item.effect)) return false;
                    return true;
                });
            },
        },
        methods: {
            useIcon,
            useLabel,
            getEffectFormula,
            // useLabel() (skillDisplay.js) vraci nelokalizovanou anglickou konstantu
            // (Attack/Dodge/Shield) - preklad pro zobrazeni (tooltip) se resi tady
            // lokalne, aby se nemusel upravovat sdileny utils soubor mimo rozsah
            // teto migrace. Filtrovaci logika v displayedItems dal porovnava s
            // puvodni anglickou hodnotou z useLabel(item), ne s timhle prekladem.
            useLabelText(item) {
                const map = {
                    Attack: this.$t('pokedex.common.useAttack'),
                    Dodge: this.$t('pokedex.common.useDodge'),
                    Shield: this.$t('pokedex.common.useShield'),
                };
                return map[useLabel(item)] || useLabel(item);
            },
            isFirst(item) {
                if (this.mode === 'buy') {
                    const skillIds = this.pokemon.pokemonType.skills.map((skill) => skill.id);
                    return item.preSkillIds.filter((id) => skillIds.indexOf(id) !== -1).length <= 0;
                }
                return item.preSkillIds.length === 0;
            },
            haveSkill(item) {
                return this.mode === 'buy' && this.pokemon.skillIds.indexOf(item.id) !== -1;
            },
            havePreSkill(item) {
                const skillIds = this.pokemon.skillIds;
                const intersects = item.preSkillIds.filter((id) => skillIds.indexOf(id) !== -1).length > 0;
                return this.isFirst(item) || intersects;
            },
            canBuy(item) {
                const haveLvl = item.lvl <= this.pokemon.level;
                const haveFreeExp = item.cost <= this.pokemon.pokemonExperience.freeExp;
                return haveLvl && haveFreeExp && this.havePreSkill(item);
            },
            rowIcon(item) {
                if (this.mode === 'buy') {
                    return this.isFirst(item) ? (this.haveSkill(item) ? 'mdi-arrow-down-circle' : 'mdi-arrow-down-bold-box') : (this.haveSkill(item) ? 'mdi-arrow-down-drop-circle-outline' : 'mdi-arrow-up-drop-circle-outline');
                }
                return this.isFirst(item) ? 'mdi-arrow-down-circle' : 'mdi-arrow-down-drop-circle-outline';
            },
            rowBg(item) {
                if (this.mode === 'buy') {
                    const color = this.haveSkill(item) ? this.colors[item.type.id] : this.colorsGrey[item.type.id];
                    const a = Math.min(1, (this.haveSkill(item) ? (this.isFirst(item) / 4 + 0.5) : (this.isFirst(item) / 4 + 0.75)) * 1.2);
                    return 'rgba(' + parseInt(color.substring(1, 3), 16) + ',' + parseInt(color.substring(3, 5), 16) + ',' + parseInt(color.substring(5, 7), 16) + ',' + a + ')';
                }
                if (this.isSkillDisabled(item)) {
                    return getRgba(this.colorsGrey[item.type.id], item);
                }
                return getRgba(this.colors[item.type.id], item);
            },
            // dungeon "uz dnes pouzity" pool (viz disabledSkillIds prop vyse) -
            // porovnani pres String() kvuli simple_array na BE (Position::
            // trainerPokemonSkills vraci pole stringu, item.id je cislo).
            isSkillDisabled(item) {
                return this.disabledSkillIds.map(String).includes(String(item.id));
            },
        },
    }
</script>

<style scoped>
    .skill-list-table__toolbar {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 4px;
    }

    .skill-list-table__filter-popup {
        width: 260px;
    }

    .theme--light .skill-list-table__filter-hint {
        color: rgba(0, 0, 0, .6);
    }
    .theme--dark .skill-list-table__filter-hint {
        color: rgba(255, 255, 255, .7);
    }

    .skill-list-table__rows {
        display: flex;
        flex-direction: column;
    }

    .skill-row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 8px;
        border-bottom: 1px solid rgba(0, 0, 0, .1);
    }

    .skill-row--clickable {
        cursor: pointer;
    }

    .skill-row--disabled {
        opacity: .55;
        filter: grayscale(.6);
    }

    .skill-row:hover {
        filter: brightness(0.97);
    }

    .skill-list-table__header-row {
        cursor: default;
        font-weight: 700;
        font-size: .72rem;
        text-transform: uppercase;
        letter-spacing: .02em;
    }
    .theme--light .skill-list-table__header-row {
        color: rgba(0, 0, 0, .55);
        border-bottom: 2px solid rgba(0, 0, 0, .25);
    }
    .theme--dark .skill-list-table__header-row {
        color: rgba(255, 255, 255, .65);
        border-bottom: 2px solid rgba(255, 255, 255, .3);
    }

    .skill-list-table__header-row .skill-row__use,
    .skill-list-table__header-row .skill-row__aoe {
        flex-basis: 30px;
    }

    .skill-row__name {
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .skill-row__name span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .skill-row__type {
        flex: 0 0 auto;
    }

    .skill-row__use,
    .skill-row__aoe {
        flex: 0 0 18px;
        text-align: center;
    }

    .skill-row__dmg {
        flex: 0 0 52px;
        text-align: right;
        font-variant-numeric: tabular-nums;
        font-size: .8rem;
    }

    .skill-row__formula,
    .skill-row__bonus,
    .skill-row__effect,
    .skill-row__cost,
    .skill-row__level {
        display: none;
        flex: 0 0 auto;
        text-align: right;
        font-size: .8rem;
        font-variant-numeric: tabular-nums;
    }

    .skill-row__buy {
        flex: 0 0 56px;
        text-align: center;
    }
    /* Zrcadleni pro praváky (flags.rightHanded) - `order` misto zmeny poradi v DOM. */
    .skill-list-table__rows--buy-left .skill-row__buy {
        order: -1;
    }

    @media (min-width: 460px) {
        .skill-row__formula {
            display: block;
            flex-basis: 56px;
        }
    }

    @media (min-width: 560px) {
        .skill-row__bonus {
            display: block;
            flex-basis: 56px;
        }
    }

    @media (min-width: 660px) {
        .skill-row__effect {
            display: block;
            flex-basis: 90px;
        }
    }

    @media (min-width: 760px) {
        .skill-row__cost {
            display: block;
            flex-basis: 46px;
        }
    }

    @media (min-width: 860px) {
        .skill-row__level {
            display: block;
            flex-basis: 32px;
        }
    }

    /* BUY mod ma jine vzdy-viditelne minimum (Name/Exp/Buy) a jine breakpointy nez VIEW mod. */
    .skill-list-table__rows--buy .skill-row__type {
        display: none;
    }
    .skill-list-table__rows--buy .skill-row__use,
    .skill-list-table__rows--buy .skill-row__aoe {
        flex-basis: 30px;
        display: none;
    }
    .skill-list-table__rows--buy .skill-row__dmg {
        flex-basis: 60px;
        display: none;
    }
    .skill-list-table__rows--buy .skill-row__cost {
        display: block;
        flex-basis: 40px;
    }
    @media (min-width: 380px) {
        .skill-list-table__rows--buy .skill-row__type {
            display: block;
        }
    }
    @media (min-width: 460px) {
        .skill-list-table__rows--buy .skill-row__formula {
            flex-basis: 64px;
        }
    }
    @media (min-width: 560px) {
        .skill-list-table__rows--buy .skill-row__use,
        .skill-list-table__rows--buy .skill-row__aoe {
            display: block;
        }
    }
    @media (min-width: 860px) {
        .skill-list-table__rows--buy .skill-row__dmg {
            display: block;
        }
    }

    .skill-list-table__rows--expanded {
        overflow-x: auto;
    }

    .skill-list-table__rows--expanded .skill-row {
        min-width: 680px;
    }

    .skill-list-table__rows--expanded.skill-list-table__rows--buy .skill-row {
        min-width: 780px;
    }

    .skill-list-table__rows--expanded .skill-row__name {
        flex: 0 0 150px !important;
    }

    .skill-list-table__rows--expanded .skill-row__formula,
    .skill-list-table__rows--expanded .skill-row__bonus,
    .skill-list-table__rows--expanded .skill-row__cost {
        display: block !important;
        flex: 0 0 56px !important;
    }

    .skill-list-table__rows--expanded .skill-row__effect {
        display: block !important;
        flex: 0 0 90px !important;
    }

    .skill-list-table__rows--expanded .skill-row__level {
        display: block !important;
        flex: 0 0 32px !important;
    }

    /* Bez pevne sirky by "type" sloupec v rozbalenem rezimu delal "rozsypany" dojem
       (flex:0 0 auto = sirka podle obsahu radku, jina radek od radku). */
    .skill-list-table__rows--expanded.skill-list-table__rows--buy .skill-row__name {
        flex: 0 0 130px !important;
    }
    .skill-list-table__rows--expanded.skill-list-table__rows--buy .skill-row__use,
    .skill-list-table__rows--expanded.skill-list-table__rows--buy .skill-row__aoe,
    .skill-list-table__rows--expanded.skill-list-table__rows--buy .skill-row__dmg {
        display: block !important;
    }
    .skill-list-table__rows--expanded.skill-list-table__rows--buy .skill-row__type {
        display: block !important;
        flex: 0 0 74px !important;
    }
    .skill-list-table__rows--expanded.skill-list-table__rows--buy .skill-row__formula {
        flex: 0 0 64px !important;
    }
</style>
